import { PasswordValidationResult } from "../types";

// Email validation
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Password validation with detailed errors
export const validatePassword = (
  password: string
): PasswordValidationResult => {
  const errors: string[] = [];

  if (password.length < 8) {
    errors.push("Password must be at least 8 characters long");
  }

  if (!/[A-Z]/.test(password)) {
    errors.push("Password must contain at least one uppercase letter");
  }

  if (!/[a-z]/.test(password)) {
    errors.push("Password must contain at least one lowercase letter");
  }

  if (!/[0-9]/.test(password)) {
    errors.push("Password must contain at least one number");
  }

  if (!/[!@#$%^&*(),.?":{}|<>_\-+=[\]\\;'/`~]/.test(password)) {
    errors.push("Password must contain at least one special character");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Password strength indicator (optional UI helper)
export const getPasswordStrength = (
  password: string
): "weak" | "medium" | "strong" => {
  const result = validatePassword(password);
  const passedChecks = 5 - result.errors.length;

  if (passedChecks <= 2) return "weak";
  if (passedChecks <= 4) return "medium";
  return "strong";
};

// First name validation
export const validateFirstName = (firstName: string): boolean => {
  return firstName.trim().length >= 1 && firstName.trim().length <= 50;
};

// Last name validation (optional field)
export const validateLastName = (lastName: string): boolean => {
  if (!lastName) return true; // Optional
  return lastName.trim().length <= 50;
};

/**
 * Validate file type
 *
 * @param file - File object
 * @param acceptedTypes - Array of accepted MIME types or extensions
 * @returns true if file type is accepted
 *
 * @example
 * validateFileType(file, ['video/mp4', 'image/jpeg', '.stl']) // true/false
 */
export const validateFileType = (
  file: File,
  acceptedTypes: string[]
): boolean => {
  // Check MIME type
  if (acceptedTypes.includes(file.type)) {
    return true;
  }

  // Check file extension (for files like .stl that may not have proper MIME types)
  const fileName = file.name.toLowerCase();
  const fileExtension = fileName.substring(fileName.lastIndexOf("."));

  // Check if any accepted type is an extension (starts with .)
  const acceptedExtensions = acceptedTypes.filter((type) =>
    type.startsWith(".")
  );
  if (acceptedExtensions.some((ext) => fileExtension === ext.toLowerCase())) {
    return true;
  }

  // Also check if extension appears at end of MIME type (e.g., "model/stl" matches ".stl")
  const mimeExtensions = acceptedTypes
    .filter((type) => type.includes("/"))
    .map((type) => "." + type.split("/").pop());
  if (
    mimeExtensions.some((ext) => ext && fileExtension === ext.toLowerCase())
  ) {
    return true;
  }

  return false;
};

/**
 * Validate file size
 *
 * @param file - File object
 * @param maxSizeMB - Maximum file size in megabytes
 * @returns Object with isValid flag and error message
 *
 * @example
 * validateFileSize(file, 100) // { isValid: true } or { isValid: false, error: '...' }
 */
export const validateFileSize = (
  file: File,
  maxSizeMB: number
): { isValid: boolean; error?: string } => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  if (file.size > maxSizeBytes) {
    return {
      isValid: false,
      error: `File size must be less than ${maxSizeMB}MB`,
    };
  }

  return { isValid: true };
};

/**
 * Get human-readable file extensions from accepted types
 *
 * @param acceptedTypes - Array of MIME types and extensions
 * @returns Array of extensions like [".mp4", ".stl", ".jpg"]
 */
export const getReadableExtensions = (acceptedTypes: string[]): string[] => {
  const extensions = new Set<string>();

  acceptedTypes.forEach((type) => {
    if (type.startsWith(".")) {
      // Already an extension
      extensions.add(type.toLowerCase());
    } else if (type.includes("/")) {
      // MIME type - extract extension from suffix
      const suffix = type.split("/").pop();
      if (suffix && !suffix.includes("-") && !suffix.includes(".")) {
        extensions.add("." + suffix.toLowerCase());
      }
    }
  });

  return Array.from(extensions);
};

/**
 * Validate file for job creation
 *
 * @param file - File object
 * @param acceptedTypes - Array of accepted MIME types
 * @param maxSizeMB - Maximum file size in megabytes
 * @returns Object with isValid flag and error message
 */
export const validateJobFile = (
  file: File | null,
  acceptedTypes: string[],
  maxSizeMB: number
): { isValid: boolean; error?: string } => {
  if (!file) {
    return { isValid: false, error: "Please select a file" };
  }

  // Validate file type
  if (!validateFileType(file, acceptedTypes)) {
    // Generate helpful error message
    const extensions = getReadableExtensions(acceptedTypes);
    const extensionList =
      extensions.length > 0 ? extensions.join(", ") : acceptedTypes.join(", ");

    return {
      isValid: false,
      error: `Invalid file type. Accepted formats: ${extensionList}`,
    };
  }

  // Validate file size
  const sizeValidation = validateFileSize(file, maxSizeMB);
  if (!sizeValidation.isValid) {
    return sizeValidation;
  }

  return { isValid: true };
};

/**
 * Validate base64 image string
 *
 * @param base64String - Base64 encoded image
 * @returns true if valid base64 image
 */
export const validateBase64Image = (base64String: string): boolean => {
  if (!base64String) return false;

  // Check for data URL prefix
  const base64Regex = /^data:image\/(png|jpeg|jpg|gif|webp);base64,/;
  return base64Regex.test(base64String);
};

/**
 * Check if string is empty or whitespace only
 *
 * @param str - String to check
 * @returns true if empty or whitespace
 */
export const isEmpty = (str: string | null | undefined): boolean => {
  return !str || str.trim().length === 0;
};

/**
 * Validate required field
 *
 * @param value - Value to validate
 * @param fieldName - Name of field for error message
 * @returns Object with isValid flag and error message
 */
export const validateRequired = (
  value: any,
  fieldName: string = "This field"
): { isValid: boolean; error?: string } => {
  if (value === null || value === undefined || isEmpty(String(value))) {
    return { isValid: false, error: `${fieldName} is required` };
  }

  return { isValid: true };
};

/**
 * Validate minimum length
 *
 * @param value - String to validate
 * @param minLength - Minimum length
 * @param fieldName - Name of field for error message
 * @returns Object with isValid flag and error message
 */
export const validateMinLength = (
  value: string,
  minLength: number,
  fieldName: string = "This field"
): { isValid: boolean; error?: string } => {
  if (value.length < minLength) {
    return {
      isValid: false,
      error: `${fieldName} must be at least ${minLength} characters`,
    };
  }

  return { isValid: true };
};

/**
 * Validate maximum length
 *
 * @param value - String to validate
 * @param maxLength - Maximum length
 * @param fieldName - Name of field for error message
 * @returns Object with isValid flag and error message
 */
export const validateMaxLength = (
  value: string,
  maxLength: number,
  fieldName: string = "This field"
): { isValid: boolean; error?: string } => {
  if (value.length > maxLength) {
    return {
      isValid: false,
      error: `${fieldName} must be less than ${maxLength} characters`,
    };
  }

  return { isValid: true };
};
